/**
* @file    stats-ranges.js
* @author  Eddie Roosenmaallen, eddie@kingsds.network
*          Matthew Palma, mpalma@kingsds.network
*          Nazila Akhavan Kharazian, nazila@kingsds.network
* @date    November 2018
*
* This module exports statistic and set functions and classes
*
* The "distributors" export holds all supported distributing functions. Each distributing function accepts
* the standard parameters k and n, the index and set size. Additional parameters describing the distribution
* follow, usually ending with a random flag. See the individual distributing function documentation for details.
* By convention, if a distributor supports random sets, the random flag should be the last parameter.
*
* The "set" export holds creator functions for each supported distribution.
*
* The "DistributionRange" export is a simple re-export of the DistributionPObject from the rangeobject module;
* this is provided as a convenience for developers who may not otherwise need to directly import rangeobject.
*
* Supported distributions:
* random(n, min, max)
* randomInt(n, min, max)      return an array of random numbers, either floating point or integers.
* identity(n)                 return the numbers from [0..n]
* poisson(n, lamda[, random])     return the poisson distribution given the mean rate of occurrence lamda. Random is supported.
* normal(n, σ, µ[, start[, end[, random]]])
*                             return a normal distribution with an sd of σ and a mean of µ. start and end may be used to
*                             define the effective range
* binomial(n, p[, random])    return a binomial distribution for a mean
*/

const { DistributionRange } = require('./range-object');

// Distributor functions; each of these will return one number from the described distribution.
// The first two parameters of the function should be [x /* the input value */, n /* total items */].

/** Return a random number between min and max
 *  @param  k                 Ignored for this dist; included for compatibility
 *  @param  n                 Ignored for this dist; included for compatibility
 *  @param  min               Lower bound of desired range
 *  @param  max               Upper bound (exclusive) of desired range
 *  @return A floating-point number min <= value < max
 */
function random(k, n, min, max) {
  return (Math.random() * (max - min)) + min
}

/** Return a random integer between min and max
 *  @param  k                 Ignored for this dist; included for compatibility
 *  @param  n                 Ignored for this dist; included for compatibility
 *  @param  min               Lower bound of desired range
 *  @param  max               Upper bound (exclusive) of desired range
 *  @return A floating-point number min <= value < max
 */
function randomInt(k, n, min, max) {
  return Math.floor(random(k, n, min, max))
}

/** Return n random number which are normally distributed
 *  @param  n                 Size of result set
 *  @param  σ                 Standard Deviation 
 *  @param  µ                 Mean value
 *  @return n floating-point numbers 
 */
function normalRNG(k, n, µ, σ) {
  let val, u, v, s, mul;
  let result = []
  let spareRandom = null;
  if (spareRandom !== null) {
    val = spareRandom;
    spareRandom = null;
  } else {
    do {
      u = Math.random() * 2 - 1;
      v = Math.random() * 2 - 1;

      s = u ** 2 + v ** 2;
    } while (s === 0 || s >= 1);

    mul = Math.sqrt(-2 * Math.log(s) / s);

    val = u * mul;
    spareRandom = v * mul;
  }

return val * σ + µ;
}

/** Return the parameter
 *  @param  k                 The value to return
 *  @param  n                 Ignored for this dist; included for compatibility
 *  @return The value k
 */
function identity(k, n) {
  return k
}

/** Poisson probability mass function
 *  @param  x                 The value to test
 *  @param  n                 Ignored for this dist; included for compatibility
 *  @param  lamda                 The mean rate of occurrence
 *  @param  random            Truthy to return probability for a random k-value between [0..n]
 *  @return The probability [0..1] of x occurrences
 */
function poisson(x, n, lamda, random) {
  if (random || x === 'random') {
    x = random(0, n)
  }

  let ans, total = 0
  if (isNaN(x) || isNaN(lamda) || lamda < 0) {
    return NaN
  }
  if (!Number.isInteger(x)) {
    return 0
  }
  if (x < 0 || !isFinite(x)) {
    return 0
  }
  x = Math.round(x)
  ans = -lamda + x * Math.log(lamda)
  for (let i = 1; i <= x; i++) {
    total += Math.log(i)
  }
  let logAns = ans - total
  return Math.exp(logAns)
}

/** Calculate the normal probability density
 *  @param  k                 Index to calculate for, or 'random' for a random value between [xB..xE]
 *  @param  n                 Size of result set; used to calculate xE for random distributions
 *  @param  µ                 @todo Some other parameter -- mean, maybe? ~ER2018-11
 *  @param  σ                 Standard Deviation of the probability curve
 *  @param  xB                Beginning of range. Defaults to zero
 *  @param  xE                End of range. Defaults to (xB + n)
 *  @param  random            Truthy to return probability at a random k-value between [xB..xE]
 *  @return Probability at k-index, or random point on a (σ,µ) normal distribution
 */
function normal(k, n, µ = 0, σ = 1, xB, xE, random) {
  let x

  if (random || k === 'random') {
    if (typeof xB === 'undefined') {
      xB = 0
    }
    if (typeof xE === 'undefined') {
      xE = xB + n
    }

    x = (Math.random() * (xE - xB)) + xB
  } else if (typeof xB === 'number' && typeof xE === 'undefined') {
    x = xB + k
  } else if (typeof xB === 'number' && typeof xE === 'number') {
    const range = xE - xB
    const step = range / n
    x = (k * step) + xB
  } else {
    x = k
  }

  return Math.pow(Math.E, -Math.pow((x - µ), 2) / 2 * (σ ** 2))
}

/** binomial distribution
 *  @param  k                 Index to calculate probability for
 *  @param  n                 Set size
 *  @param  p                 Mean probability?
 *  @param  random            Truthy to return probability for a random k-value between [0..n]
 *  @return Probability at k-index
 */
function binomial(k, n, p, random) {
  if (random || k === 'random') {
    k = random(0, n)
  }

  if (k > n) {
    return NaN
  }

  var logBinom = logsum(n - k + 1, n) - logsum(2, k) + k * Math.log(p) + (n - k) * Math.log(1 - p)
  return Math.exp(logBinom)
}

// Support functions
function factorial(n) {
  return n < 2 ? 1 : n * factorial(n - 1)
}

function logsum(lowerBound, upperBound) {
  var sum = 0
  for (let i = lowerBound; i <= upperBound; i++) {
    sum += Math.log(i)
  }
  return sum
}

var distributor = {
  random,
  randomInt,
  identity,
  poisson,
  normal,
  binomial,
  normalRNG,
}
var set = {
  random: (n, min, max) => new DistributionRange(n, 'random', min, max),
  randomInt: (n, min, max) => new DistributionRange(n, 'randomInt', min, max),
  identity: (n) => new DistributionRange(n, 'identity'),
  poisson: (n, lamda = 1, random) => new DistributionRange(n, 'poisson', lamda, random),
  normal: (n, µ = 0, σ = 1, start, end, random) => new DistributionRange(n, 'normal', µ, σ, start, end, random),
  binomial: (n, p = 0.5, random) => new DistributionRange(n, 'binomial', p, random),
  normalRNG: (n, µ = 0, σ = 1) => new DistributionRange(n, 'normalRNG', µ, σ)
}

Object.assign(exports, {
  distributor,
  set,
});
